import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { removePost, setPost } from "state";
import {
  MoreHoriz,
  FavoriteBorderOutlined,
  Favorite,
  Close,
  CheckCircle,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  IconButton,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Modal,
  Menu,
  MenuItem,
  Snackbar,
  Tooltip,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { resolveAssetUrl } from "utils/media";
import PostEditorDialog from "./PostEditorDialog";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

const countComments = (comments = []) =>
  (Array.isArray(comments) ? comments : []).reduce((sum, comment) => {
    if (!comment || typeof comment !== "object") return sum + 1;
    return sum + 1 + countComments(comment.replies || []);
  }, 0);

const getCommentLabel = (count) => (count === 1 ? "Kommentar" : "Kommentare");

const PostWidget = ({
  postId,
  postUserId,
  firstName,
  lastName,
  username,
  artistName = "",
  school = "",
  authorSchool = "",
  isOfficial = false,
  userPicturePath,
  caption,
  picturePath,
  imageUrls = [],
  likes,
  comments = [],
  createdAt,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((state) => state.token);
  const user = useSelector((state) => state.user);
  const loggedInUserId = user?._id;
  const isOwnPost = loggedInUserId === postUserId;
  const canEditPost = Boolean(isOwnPost || user?.isAdmin);
  const canDeletePost = Boolean(isOwnPost || user?.isAdmin);

  const [optimisticLikes, setOptimisticLikes] = useState(likes || {});
  const likeMap = optimisticLikes || {};
  const isPostLiked = loggedInUserId ? Boolean(likeMap[loggedInUserId]) : false;
  const displayName = artistName || `@${username || "profil"}`;
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [postMenuAnchorEl, setPostMenuAnchorEl] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [isLikeSaving, setIsLikeSaving] = useState(false);

  const likeCount = Object.keys(likeMap).length;
  const commentCount = countComments(comments);

  const postImages = useMemo(() => {
    if (Array.isArray(imageUrls) && imageUrls.length > 0) return imageUrls;
    if (picturePath) return [picturePath];
    return [];
  }, [imageUrls, picturePath]);

  useEffect(() => {
    setOptimisticLikes(likes || {});
  }, [likes]);

  const patchLike = async () => {
    if (!loggedInUserId || !token) {
      setLoginModalOpen(true);
      return;
    }
    if (isLikeSaving) return;

    let previousLikes = {};
    let nextLikes = {};
    setOptimisticLikes((currentLikes) => {
      const safeLikes = currentLikes || {};
      previousLikes = safeLikes;
      nextLikes = { ...safeLikes };
      if (nextLikes[loggedInUserId]) {
        delete nextLikes[loggedInUserId];
      } else {
        nextLikes[loggedInUserId] = true;
      }
      return nextLikes;
    });
    setIsLikeSaving(true);

    try {
      const response = await fetch(`${BASE_URL}/posts/${postId}/like`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        throw new Error("Like konnte nicht gespeichert werden.");
      }

      const updatedPost = await response.json();
      setOptimisticLikes(updatedPost?.likes || nextLikes);
      dispatch(setPost({ post: updatedPost }));
    } catch (error) {
      setOptimisticLikes(previousLikes);
      setSnackbarState({
        open: true,
        message: "Like konnte nicht gespeichert werden.",
        severity: "error",
      });
    } finally {
      setIsLikeSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!token || !canDeletePost) return;

    const response = await fetch(`${BASE_URL}/posts/${postId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) return;

    dispatch(removePost({ postId }));
    setIsDeleteDialogOpen(false);
    setPostMenuAnchorEl(null);
  };

  const handleShare = async () => {
    const postLink = `${window.location.origin}/wettbewerb/post/${postId}`;
    try {
      await navigator.clipboard.writeText(postLink);
      setSnackbarState({
        open: true,
        message: "Link wurde in die Zwischenablage kopiert.",
        severity: "success",
      });
    } catch (error) {
      setSnackbarState({
        open: true,
        message: postLink,
        severity: "info",
      });
    } finally {
      setPostMenuAnchorEl(null);
    }
  };

  const handleEditSaved = (updatedPost) => {
    dispatch(setPost({ post: updatedPost }));
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "background.paper",
    borderRadius: "8px",
    boxShadow: 24,
    p: 4,
  };

  return (
    <>
      <Box bgcolor="background.alt" borderRadius="12px" overflow="hidden">
        <Box display="flex" alignItems="center" gap={1} p={1}>
          <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1 }}>
            <Avatar src={resolveAssetUrl(userPicturePath)} sx={{ width: 36, height: 36 }} />
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={600} fontSize="0.95rem">
                <Box
                  component={RouterLink}
                  to={username ? `/u/${username}` : `/profile/${postUserId}`}
                  sx={{
                    display: "inline",
                    lineHeight: "inherit",
                    color: "inherit",
                    textDecoration: "none",
                    "&:visited, &:active": {
                      color: "inherit",
                      textDecoration: "none",
                    },
                    "&:hover": {
                      color: "inherit",
                      textDecoration: "underline",
                    },
                  }}
                >
                  {displayName}
                </Box>
                {isOfficial && (
                  <Tooltip
                    title="Verifiziert"
                    slotProps={{
                      tooltip: {
                        sx: {
                          borderRadius: "999px",
                        },
                      },
                    }}
                  >
                    <CheckCircle
                      sx={{
                        ml: 0.5,
                        fontSize: "0.95rem",
                        color: "text.secondary",
                        verticalAlign: "middle",
                      }}
                    />
                  </Tooltip>
                )}
              </Typography>
              <Typography fontSize="0.8rem" color="text.secondary">
                <Box
                  component={RouterLink}
                  to={username ? `/u/${username}` : `/profile/${postUserId}`}
                  sx={{
                    display: "inline",
                    lineHeight: "inherit",
                    color: "text.secondary",
                    textDecoration: "none",
                    "&:visited, &:active": {
                      color: "text.secondary",
                      textDecoration: "none",
                    },
                    "&:hover": {
                      color: "text.secondary",
                      textDecoration: "underline",
                    },
                  }}
                >
                  @{username || "profil"}
                </Box>
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={(event) => setPostMenuAnchorEl(event.currentTarget)}>
            <MoreHoriz />
          </IconButton>
          <Menu
            anchorEl={postMenuAnchorEl}
            open={Boolean(postMenuAnchorEl)}
            onClose={() => setPostMenuAnchorEl(null)}
            disableScrollLock
          >
            <MenuItem onClick={handleShare}>Teilen</MenuItem>
            {canEditPost && (
              <MenuItem
                onClick={() => {
                  setIsEditDialogOpen(true);
                  setPostMenuAnchorEl(null);
                }}
              >
                Bearbeiten
              </MenuItem>
            )}
            {canDeletePost && (
              <MenuItem
                onClick={() => {
                  setIsDeleteDialogOpen(true);
                  setPostMenuAnchorEl(null);
                }}
              >
                Löschen
              </MenuItem>
            )}
          </Menu>
        </Box>
        <Box
          onClick={() => navigate(`/wettbewerb/post/${postId}`)}
          sx={{ cursor: "pointer", position: "relative", width: "100%", "&:after": { content: '""', display: "block", paddingBottom: "100%" } }}
        >
          {postImages[0] && (
            <img
              src={resolveAssetUrl(postImages[0])}
              alt="post"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          )}
        </Box>
        <Box p={1}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <IconButton onClick={patchLike} disabled={isLikeSaving}>
              {isPostLiked ? <Favorite color="error" /> : <FavoriteBorderOutlined />}
            </IconButton>
            <Typography fontWeight={600}>{likeCount}</Typography>
            <Typography fontSize="0.8rem" color="text.secondary" ml="auto">
              {commentCount} {getCommentLabel(commentCount)}
            </Typography>
          </Box>
          <Typography fontSize="0.9rem" sx={{ mt: 0.5 }}>
            {caption || ""}
          </Typography>
          {(createdAt || school) && (
            <Box mt={0.4} display="flex" alignItems="center" justifyContent="space-between" gap={1}>
              <Typography fontSize="0.75rem" color="text.secondary">
                {createdAt ? new Date(createdAt).toLocaleDateString("de-DE") : ""}
              </Typography>
              <Typography
                fontSize="0.75rem"
                color="text.secondary"
                textAlign="right"
                sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {school || ""}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <PostEditorDialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        token={token}
        postId={postId}
        initialCaption={caption || ""}
        initialSchool={school || ""}
        initialAuthorSchool={authorSchool || ""}
        initialImageUrls={postImages}
        onSaved={handleEditSaved}
      />

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Beitrag löschen?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            Dieser Beitrag wird dauerhaft gelöscht und kann nicht wiederhergestellt werden.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>Abbrechen</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Login Modal */}
      <Modal
        open={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        disableScrollLock={true}
      >
        <Box sx={modalStyle}>
          <Box display="flex" justifyContent="flex-end">
            <IconButton onClick={() => setLoginModalOpen(false)}>
              <Close />
            </IconButton>
          </Box>
          <Typography variant="h6" align="center" gutterBottom>
            Du musst eingeloggt sein, um Kaleidoskope zu liken.
          </Typography>
          <Box display="flex" justifyContent="center">
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                navigate("/einloggen");
              }}
            >
              Einloggen
            </Button>
          </Box>
        </Box>
      </Modal>
      <Snackbar
        open={snackbarState.open}
        autoHideDuration={4000}
        onClose={() =>
          setSnackbarState((prev) => ({
            ...prev,
            open: false,
          }))
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() =>
            setSnackbarState((prev) => ({
              ...prev,
              open: false,
            }))
          }
          severity={snackbarState.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PostWidget;
